import React, { useEffect, useRef, useState } from 'react';

const useReveal = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.1 });

    if (currentRef) observer.observe(currentRef);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

const About = () => {
  const [whoRef, whoVisible] = useReveal();
  const [communityRef, communityVisible] = useReveal();
  const [divider1Ref, divider1Visible] = useReveal();
  const [visionRef, visionVisible] = useReveal();

  return (
    <div className="my-8 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Who We Are section */}
      <section 
        ref={whoRef}
        className={`transition-all duration-700 relative overflow-hidden ${whoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 relative z-10">
          <div className={`md:w-2/5 flex flex-col justify-center text-left transition-all duration-700 delay-200 ${whoVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <h2 className="text-3xl sm:text-4xl text-[#703aa4] leading-tight mb-4">Who We Are</h2>
            <p className="text-xl leading-relaxed text-gray-600">
              Energy Democracy exists to democratise the transition to a low carbon economy, helping individuals to save power, save money, and save the planet. We establish locally owned renewable energy co-operatives and build, operate and maintain renewable energy parks for the co-ops.
            </p>
          </div>
          <div className={`md:w-3/5 transition-all duration-700 delay-400 ${whoVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <video
              src="/media/intro-small.mp4"
              title="Energy Democracy introduction video"
              controls
              preload="metadata"
              className="w-full max-w-2xl mx-auto"
            />
          </div>
        </div>
      </section>

      {/* Enabling communities section */}
      <section 
        ref={communityRef}
        className={`my-16 transition-all duration-700 ${communityVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
          <div className={`md:w-3/5 flex flex-col justify-center text-left transition-all duration-700 delay-200 ${communityVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <h2 className="text-3xl text-[#ff8400] leading-tight mb-4">Enabling communities to combat climate change</h2>
            <p className="text-xl leading-relaxed text-gray-600">
              The world is facing a climate emergency. You can make a difference through a climate positive investment.
              <br /><br />
              Energy Democracy's community energy model engages the wider community through renewable energy initiatives to achieve tangible benefits for the climate. We build more resilient communities in this climate-impacted world and empower people to understand how they are using energy to provide social, economic and environmental benefits.
            </p>
          </div>
          <div className={`md:w-2/5 flex justify-center md:justify-end transition-all duration-700 delay-400 ${communityVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <img
              src="/media/pexels-pixabay-356036.webp" 
              alt="Solar panels generating renewable energy"
              className="w-full h-auto md:max-w-sm shadow-md"
            />
          </div>
        </div>
      </section>

      {/* Divider */}
      <div 
        ref={divider1Ref}
        className={`flex justify-center my-16 transition-all duration-500 ${divider1Visible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}
      >
        <img
          src="/media/logo-icon.svg" 
          alt=""
          aria-hidden="true"
          className="w-10 h-10"
        />
      </div>

      {/* Vision Section */}
      <section 
        ref={visionRef}
        className={`mt-16 mb-0 transition-all duration-700 ${visionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
          <div className={`md:w-2/5 flex justify-center md:justify-start transition-all duration-700 delay-200 ${visionVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <img
              src="/media/Alan.webp"
              alt="Alan Major"
              className="w-full h-auto md:max-w-sm shadow-md"
            />
          </div>
          <div className={`md:w-3/5 text-left transition-all duration-700 delay-400 ${visionVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <h2 className="text-3xl text-[#ff8400] leading-tight mb-4">Vision</h2>
            <p className="text-xl leading-relaxed text-gray-600">
              Energy Democracy is the vision of Alan Major. After restructuring the Northern Territory YHA in 2008 to ensure the movement's sustainability, Alan was challenged to not just get another job, but to do something that would change the world.
              <br /><br />
              Alan chose to commit his efforts to improving the uptake of renewable energy to help reduce the impact of climate change. He observed the rapid uptake of rooftop solar PV in Australia but noticed there are a lot of people missing out, simply because they don't have a suitable roof.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
